// ─── ErrorBoundary ──────────────────────────────────────────────────────
// Filet anti-écran-noir + diagnostic pour le cockpit SaaS.
//
// Un crash de rendu React non rattrapé démonte tout l'arbre → écran noir
// sans message. Cette boundary intercepte l'erreur, la LOGGE en console
// (avec le componentStack) et l'AFFICHE lisiblement à l'écran — le but est
// diagnostique : ne JAMAIS masquer l'erreur, la montrer.
//
// Style volontairement sobre : le cockpit praticienne est fonctionnel et
// dense, pas décoré. Pas d'emoji superflu, pas d'effet.
//
// Usage :
//   <ErrorBoundary>            → filet global (racine main.jsx)
//   <ErrorBoundary>            → local, autour du corps d'une modale, pour
//                                qu'un crash pendant la génération s'affiche
//                                DANS la modale sans tuer tout le cockpit.

import { Component } from 'react';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Log complet en console : message + stack + componentStack. Indispensable
    // pour retrouver le composant fautif sans patcher à l'aveugle.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] crash de rendu', error, info?.componentStack);
  }

  handleReset() {
    // Reset l'état de la boundary → tente un re-render du sous-arbre. Utile si
    // l'erreur était transitoire (ex: état intermédiaire pendant une génération).
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    // Stack tronqué pour rester lisible (les 12 premières lignes suffisent au
    // diagnostic ; le reste est dans la console).
    const stack = typeof error.stack === 'string'
      ? error.stack.split('\n').slice(0, 12).join('\n')
      : null;

    return (
      <div
        role="alert"
        style={{
          margin: 16,
          padding: 18,
          borderRadius: 10,
          background: 'rgba(220, 80, 80, 0.08)',
          border: '1px solid rgba(220, 80, 80, 0.35)',
          color: '#f0d4d4',
          fontSize: '.86rem',
          lineHeight: 1.5,
        }}
      >
        <h3 style={{ margin: '0 0 10px', color: '#f5c6c6', fontSize: '1rem', fontWeight: 700 }}>
          Une erreur est survenue
        </h3>

        <div style={{ marginBottom: 10, color: '#f0d4d4' }}>
          <strong>Message :</strong>{' '}
          <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
            {error?.message || String(error)}
          </span>
        </div>

        {stack && (
          <pre
            style={{
              margin: '0 0 14px',
              padding: 12,
              borderRadius: 6,
              background: 'rgba(0, 0, 0, 0.30)',
              color: '#d9b8b8',
              fontFamily: 'ui-monospace, SFMono-Regular, monospace',
              fontSize: '.72rem',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowX: 'auto',
            }}
          >
            {stack}
          </pre>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={this.handleReset}
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              color: '#e8ddc0',
              padding: '7px 16px',
              borderRadius: 6,
              fontSize: '.82rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Réessayer
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              color: '#a8a898',
              padding: '7px 16px',
              borderRadius: 6,
              fontSize: '.82rem',
              cursor: 'pointer',
            }}
          >
            Recharger la page
          </button>
        </div>
      </div>
    );
  }
}
